/**
 * ATTRACT OVERLAY (B6): the kiosk's captions and "Touch anywhere to begin", spec §G.
 *
 *   <AttractOverlay/>   in the layout's overlays slot; renders only while phase === "attract"
 *
 * - Captions (from the attract loop via attractState): a glass card, bottom-centre of the camera's
 *   safe area, above the pill, max 720 wide: kicker in .ld-meta 13 over a line in Newsreader 30/360.
 *   A new caption enters 640 settle while the previous leaves 448 leave, in the same place.
 * - "Touch anywhere to begin": glass pill, 56 high, .ld-label × 1.2; a sun ring breathes round its icon
 *   (breathe, 2400 ms, scale 1 → 1.08, opacity .6 → 1). Shown throughout attract.
 * - Enter: the scene's edges settle into dusk over 640 drift (the soft clear happens under it).
 *   Wake: everything leaves over 294 leave.
 * - Never takes pointers: the waking touch is consumed by useKiosk at the window, so it cannot click
 *   through. Not a live region (announcements are suppressed in attract, §G).
 */
import { useEffect, useState, type CSSProperties, type JSX } from "react";
import { Icon, cx, exitMs, DUR } from "@/design";
import { useApp } from "@/store/store";
import { useAttractState } from "./attractState";
import type { Caption } from "./script";
import "./kiosk.css";

export const TOUCH_COPY = "Touch anywhere to begin";
/** The attract content is one labelled region (axe "region": no content outside a landmark). */
export const ATTRACT_LABEL = "What's around Ládera City";

/** Overlay exit on wake: 0.7 × 420. Caption exit: 0.7 × 640. */
const LEAVE_MS = exitMs(DUR.calm);
const CAPTION_OUT_MS = exitMs(DUR.unhurried);
/** "Touch anywhere to begin": 56 in the kiosk (§G), 48 in a desktop preview of attract. */
const PILL_H = 56;
const PILL_H_DESKTOP = 48;

/** §A.6 right inset with no card open (G + Z + B): kiosk 128, large desktop 112, desktop 104. */
function restRight(layout: string): number {
  if (layout === "kiosk") return 128;
  return typeof window !== "undefined" && window.innerWidth >= 1680 ? 112 : 104;
}

function CaptionCard({ c, out }: { c: Caption; out?: boolean }): JSX.Element {
  return (
    <div className={cx("ld-glass ld-loc-kiosk-cap", out && "is-out")}>
      <p className="ld-meta ld-loc-kiosk-kicker">{c.kicker}</p>
      <p className="ld-loc-kiosk-line">{c.line}</p>
    </div>
  );
}

export function AttractOverlay(): JSX.Element | null {
  const phase = useApp((s) => s.phase);
  const insets = useApp((s) => s.insets);
  const layout = useApp((s) => s.layout);
  const { caption } = useAttractState();
  const active = phase === "attract";

  /* presence: stays mounted for the 294 ms exit after a wake */
  const [present, setPresent] = useState(active);
  const [leaving, setLeaving] = useState(false);
  if (active && (!present || leaving)) {
    setPresent(true);
    setLeaving(false);
  } else if (!active && present && !leaving) {
    setLeaving(true);
  }
  useEffect(() => {
    if (!leaving) return undefined;
    const t = window.setTimeout(() => {
      setPresent(false);
      setLeaving(false);
    }, LEAVE_MS);
    return () => window.clearTimeout(t);
  }, [leaving]);

  /* captions: the new one enters while the previous one leaves */
  const [shown, setShown] = useState<Caption | null>(caption);
  const [gone, setGone] = useState<Caption | null>(null);
  if (caption !== shown) {
    if (shown) setGone(shown);
    setShown(caption);
  }
  useEffect(() => {
    if (!gone) return undefined;
    const t = window.setTimeout(() => setGone(null), CAPTION_OUT_MS);
    return () => window.clearTimeout(t);
  }, [gone]);

  if (!present) return null;
  // Both centred in the camera's safe area as it is with no card open, so they keep one place while the
  // card comes and goes (and the pill never slides onto the LightIsland). The pill sits on the chrome row,
  // level with the LightIsland (never below 24); the caption floats above the chrome, clear of its top edge.
  const right = Math.min(insets.right, restRight(layout));
  const pillH = layout === "kiosk" ? PILL_H : PILL_H_DESKTOP;
  const pillBottom = Math.max(24, insets.bottom - 80);
  const capBottom = Math.max(pillBottom + pillH + 16, insets.bottom + 20);
  const pillDock: CSSProperties = { left: insets.left, right, bottom: pillBottom };
  const capDock: CSSProperties = { left: insets.left, right, bottom: capBottom };

  return (
    <section className={cx("ld-loc-kiosk-attract", leaving && "is-leaving")} data-layout={layout} aria-label={ATTRACT_LABEL}>
      <div className="ld-vignette ld-loc-kiosk-veil" aria-hidden="true" />
      <div className="ld-loc-kiosk-dock" style={capDock}>
        <div className="ld-loc-kiosk-caps">
          {gone && gone !== shown && <CaptionCard key={`out-${gone.key}`} c={gone} out />}
          {shown && <CaptionCard key={shown.key} c={shown} />}
        </div>
      </div>
      <div className="ld-loc-kiosk-dock" style={pillDock}>
        <div className="ld-glass ld-loc-kiosk-touch" style={{ height: pillH }}>
          <span className="ld-loc-kiosk-mark" aria-hidden="true">
            <span className="ld-loc-kiosk-ring" />
            <Icon name="sun" size={20} />
          </span>
          <span className="ld-label ld-loc-kiosk-touch-label">{TOUCH_COPY}</span>
        </div>
      </div>
    </section>
  );
}
